import { useCanWrite, type PermissionSection } from '@artic/api-client';
import { Alert, AlertIcon } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Permissions v1: renders `children` untouched for a user with write access
 * to `section`; for a read-only user, wraps them in a disabled `<fieldset>`
 * (turning off every native control and triggering Chakra's `isDisabled`
 * styling via `:disabled`) and, by default, shows an info banner above them.
 *
 * What the fieldset does NOT cover:
 * - Portaled content (Chakra `Modal` / `Menu` / `Popover` / `Drawer`) renders
 *   outside it, in a portal appended to `document.body`. It stays safe only
 *   while every trigger that OPENS the portal sits inside the fieldset — a
 *   floating action button rendered elsewhere needs its own `useCanWrite`
 *   check.
 * - Handlers fired outside a form control (drag-and-drop, keyboard
 *   shortcuts, autosave on blur of a non-form element).
 * Hide a write affordance that lives outside the wrapped content with
 * `useCanWrite(section)` directly.
 */
export function ReadOnlySection({
  section,
  children,
  banner = true,
}: {
  section: PermissionSection;
  children: ReactNode;
  /** Show the read-only banner above the content (default true). */
  banner?: boolean;
}) {
  const canWrite = useCanWrite(section);

  if (canWrite) return <>{children}</>;

  return (
    <fieldset disabled style={{ display: 'contents' }} data-testid="read-only-section">
      {banner && <ReadOnlyBanner section={section} />}
      {children}
    </fieldset>
  );
}

/**
 * Standalone permissions-v1 banner for pages that want a single notice at
 * the top rather than wrapping all of their content in `ReadOnlySection`.
 * Renders nothing for a user with write access to `section`.
 */
export function ReadOnlyBanner({ section }: { section: PermissionSection }) {
  const { t } = useTranslation(['translation']);
  const canWrite = useCanWrite(section);

  if (canWrite) return null;

  return (
    <Alert status="info" data-testid="read-only-banner">
      <AlertIcon />
      {t('permissions.read_only_notice')}
    </Alert>
  );
}
